import { type ReactNode, useRef } from "react";

/**
 * Swiping a row left shows one action behind it, as iOS's `AdminSwipeAction`:
 * a strip that scrolls sideways and snaps, so the list around it still scrolls.
 * The same action is in the row's menu, which is how a keyboard reaches it.
 */
export function SwipeAction({
  enabled,
  label,
  icon,
  onAction,
  children,
}: {
  enabled: boolean;
  label: string;
  icon: ReactNode;
  onAction: () => void;
  children: ReactNode;
}) {
  const strip = useRef<HTMLDivElement>(null);
  // One tree whether or not it swipes, so a row that turns busy keeps the focus inside it.
  return (
    <div
      ref={strip}
      className={
        enabled
          ? "flex snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          : "flex"
      }
    >
      <div className="w-full shrink-0 snap-start">{children}</div>
      {enabled && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => {
            // At once, before the row turns busy and stops scrolling: a refused action leaves the row closed.
            if (strip.current) strip.current.scrollLeft = 0;
            onAction();
          }}
          className="flex w-[84px] shrink-0 snap-end flex-col items-center justify-center gap-1 rounded-row bg-danger text-xs font-semibold text-white"
        >
          {icon}
          {label}
        </button>
      )}
    </div>
  );
}
